import * as React from "react";
import ModalPopup from "../commons/components/modal/ModalPopup";
import PatientInsuranceInformationPanel from "./PatientInsuranceInformationPanel";
import PatientHealthHistoryInformationPanel from "./PatientHealthHistoryInformationPanel";
import PatientDentalInformationPanel from "./PatientDentalInformationPanel";
import PatientAuthorizedMembersPanel from "./PatientAuthorizedMembersPanel";
import {
  PatientAndAccountInformation,
  PatientInformation,
} from "../commons/model/patient/PatientInfo";
import { InsuranceInfo } from "../patient-portal/InsuranceInfo";
import { AuthorizationInfo } from "../patient-portal/AuthorizationInfo";
import { HealthHistoryInfo } from "../patient-portal/HealthHistoryInfo";
import { DentalInfo } from "../patient-portal/DentalInfo";
import "./PatientFullDetails.scss";
import { HouseholdInfo } from "../patient-portal/HouseholdInfo";
import HouseholdInformationPanel from "./HouseholdInformationPanel";
import PatientInsuranceInfoStatusManager from "./PatientInsuranceInfoStatusManager";
import { getFullPatientLabelledData } from "../patient-profile/PatientData";
import PatientCard from "../patient-profile/PatientCard";
import TitledPanel from "../commons/components/TitledPanel";
import EntitySettingsId from "../commons/model/EntitySettingsId";
import { createRoot } from "react-dom/client";

export interface PatientPersonalInformation {
  patientInformation: PatientInformation;
  legalGuardiansInformation: PatientAndAccountInformation[];
}

export interface PatientInsuranceInformation {
  primaryInsuranceInformation?: InsuranceInfo;
  secondaryInsuranceInformation?: InsuranceInfo;
}

export interface PatientDentalInformation {
  dentalInformation?: DentalInfo;
}

export interface PatientHealthHistoryInformation {
  healthHistoryInformation?: HealthHistoryInfo;
}

export interface PatientAuthorizationInformation {
  authorizationInformation?: AuthorizationInfo;
}

export interface PatientHouseholdInformation {
  householdInformation?: HouseholdInfo;
}

export default function PatientFullDetails(props: {
  patientPersonalInformation?: PatientPersonalInformation;
  patientInsuranceInformation?: PatientInsuranceInformation;
  patientDentalInformation?: PatientDentalInformation;
  patientHealthHistoryInformation?: PatientHealthHistoryInformation;
  patientAuthorizationInformation?: PatientAuthorizationInformation;
  patientHouseholdInformation?: PatientHouseholdInformation;
  patientManagementURL: string;
  patientInsuranceInfoStatusUpdateURL: string;
  accountCreationInvitationURL?: string;
  currentDoctorUid: EntitySettingsId;
  showLeadStatus: boolean;
}) {
  return (
    <ModalPopup
      closeModal={() => document.location.assign(props.patientManagementURL)}
      fullscreen={true}
    >
      <div className="v-flex centered">
        <div className="modal-title">
          {props.patientPersonalInformation.patientInformation.fullName}
        </div>
        <div className="modal-content">
          <div className="patient-items-container">
            {props.patientPersonalInformation && (
              <div className="profile-information-item">
                <TitledPanel title={i18n["Page.Patient.FullDetails.PersonalInformation.Title"]}>
                  <PatientCard
                    patient={props.patientPersonalInformation.patientInformation}
                    patientData={getFullPatientLabelledData}
                    legalGuardians={props.patientPersonalInformation.legalGuardiansInformation}
                    invitationConfiguration={
                      props.accountCreationInvitationURL && {
                        currentDoctorUid: props.currentDoctorUid,
                        accountCreationInvitationURL: props.accountCreationInvitationURL,
                      }
                    }
                    showLeadStatus={props.showLeadStatus}
                  />
                </TitledPanel>
              </div>
            )}
            {props.patientHouseholdInformation && (
              <div className="household-information-item">
                <HouseholdInformationPanel
                  title={i18n["Page.Patient.FullDetails.HouseholdInformation.Title"]}
                  householdInformation={props.patientHouseholdInformation.householdInformation}
                />
              </div>
            )}

            {props.patientInsuranceInformation && (
              <div className="insurance-information-item">
                <PatientInsuranceInformationPanel
                  insuranceInformation={
                    props.patientInsuranceInformation.primaryInsuranceInformation
                  }
                  secondaryInsuranceInfo={
                    props.patientInsuranceInformation.secondaryInsuranceInformation
                  }
                  title={i18n["Page.Patient.FullDetails.InsuranceInformation.Title"]}
                />
                {props.patientInsuranceInfoStatusUpdateURL && (
                  <PatientInsuranceInfoStatusManager
                    status={props.patientPersonalInformation.patientInformation.insuranceInfoStatus}
                    patientInsuranceInfoUpdateURL={props.patientInsuranceInfoStatusUpdateURL}
                  />
                )}
              </div>
            )}
            <div className="v-flex other-information">
              {props.patientHealthHistoryInformation && (
                <div>
                  <PatientHealthHistoryInformationPanel
                    title={i18n["Page.Patient.FullDetails.HealthHistoryInformation.Title"]}
                    healthHistoryInformation={
                      props.patientHealthHistoryInformation.healthHistoryInformation
                    }
                  />
                </div>
              )}
              {props.patientDentalInformation && (
                <div>
                  <PatientDentalInformationPanel
                    dentalInformation={props.patientDentalInformation.dentalInformation}
                    title={i18n["Page.Patient.FullDetails.DentalInformation.Title"]}
                  />
                </div>
              )}
              {props.patientAuthorizationInformation && (
                <div>
                  <PatientAuthorizedMembersPanel
                    authorizationInformation={
                      props.patientAuthorizationInformation.authorizationInformation
                    }
                    title={i18n["Page.Patient.FullDetails.AuthorizationInformation.Title"]}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalPopup>
  );
}

PatientFullDetails.loadComponent = (elementId: string, props) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<PatientFullDetails {...props} />);
};
